import React from "react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { relatedStories, relatedReports, fmtDate, people as allPeople } from "../../data/impactStories";
import { CalendarDays, MapPin, FileText, ArrowUpRight } from "lucide-react";

export default function StoryModal({ story, onClose, onOpenStory }) {
  if (!story) return null;
  const related = relatedStories(story);
  const docs = relatedReports(story);
  const linkedPeople = allPeople.filter((p) => story.people.includes(p.name));

  return (
    <Dialog open={!!story} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="story-modal"
        className="max-w-3xl w-[94vw] max-h-[88vh] overflow-y-auto bg-[#0e0e12] border-white/10 p-0 gap-0"
      >
        <DialogTitle className="sr-only">{story.title}</DialogTitle>
        <div className="relative h-64 sm:h-80">
          <img src={story.image} alt={story.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e12] via-black/30 to-transparent" />
          <div className="absolute bottom-5 left-6 right-6">
            <div className="flex flex-wrap gap-2 mb-3">
              {story.categories.map((c) => (
                <span key={c} className="px-2.5 py-1 rounded-md bg-[#F5D000] text-black text-[10px] uppercase tracking-[0.18em] font-bold">{c}</span>
              ))}
            </div>
            <h2 className="font-display text-2xl sm:text-4xl font-bold text-white leading-tight">{story.title}</h2>
          </div>
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/55 mb-6">
            <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#F5D000]" />{fmtDate(story.date)}</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#F5D000]" />{story.location.name}</span>
            <span className="text-white/40">{story.project} · {story.client}</span>
          </div>

          <p className="text-white/80 leading-relaxed mb-6">{story.body}</p>

          {story.quote && (
            <blockquote className="border-l-2 border-[#F5D000] pl-5 py-1 text-lg text-white/90 italic font-display mb-6">
              “{story.quote}”
            </blockquote>
          )}

          {linkedPeople.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-8">
              {linkedPeople.map((p) => (
                <div key={p.id} className="flex items-center gap-3 pr-4 rounded-full bg-white/5 border border-white/10">
                  <span className="w-9 h-9 rounded-full bg-[#F5D000] flex items-center justify-center font-heavy text-xs text-black">{p.initials}</span>
                  <span className="text-sm text-white/80">{p.name}</span>
                </div>
              ))}
            </div>
          )}

          {docs.length > 0 && (
            <div className="mb-8">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-3">Supporting documents</div>
              <div className="flex flex-wrap gap-2">
                {docs.map((d) => (
                  <span key={d.id} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-xs text-white/75">
                    <FileText className="w-3.5 h-3.5 text-[#F5D000]" /> {d.title}
                  </span>
                ))}
              </div>
            </div>
          )}

          {related.length > 0 && (
            <div className="pt-6 border-t border-white/8">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-4">Related stories</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {related.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`related-story-${r.id}`}
                    onClick={() => onOpenStory(r)}
                    className="group text-left rounded-xl overflow-hidden border border-white/8 bg-white/[0.03] hover:border-[#F5D000]/50 transition-all"
                  >
                    <div className="h-20 overflow-hidden">
                      <img src={r.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-3">
                      <div className="text-xs font-semibold text-white leading-snug line-clamp-2">{r.title}</div>
                      <div className="mt-1.5 flex items-center gap-1 text-[10px] text-[#F5D000] uppercase tracking-widest">
                        Read <ArrowUpRight className="w-3 h-3" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
